
import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  X,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { motion } from "framer-motion";
import { FeedTopSection } from "../../components/feed-filters/FeedTopSection";
import type { FilterState } from "../../components/feed-filters/FeedFilters";

import { useAuth } from "../../auth/AuthContext";
import { fetchLeaks, fetchSectors } from "../../api/leaks";
import { fetchForums } from "../../api/status";
import type { LeakQueryParams } from "../../api/leaks";
import type { ForumStat } from "../../api/status";

import LeakTable from "../../components/LeakTable";
import Pagination from "../../components/intelligence/Pagination";
import { getDisplayForum } from "../../utils/cleaners";

const PAGE_SIZE = 20;

// ── Styled FilterDropdown ─────────────────────────────────────────────────────
interface FilterDropdownProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

const FilterDropdown = ({ label, options, value, onChange }: FilterDropdownProps) => {
  const isActive = value !== "All";
  return (
    <div className="filter-dropdown-wrap">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`filter-dropdown-select${isActive ? " filter-dropdown-active" : ""}`}
      >
        <option value="All">{label}: All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <ChevronDown
        className="filter-dropdown-chevron"
        size={14}
      />
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Leaks() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [forums, setForums] = useState<ForumStat[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [filterValue, setFilterValue] = useState("");
  const [debouncedFilterValue, setDebouncedFilterValue] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const [sectorFilter, setSectorFilter] = useState<string>(searchParams.get("sector") || "All");
  const [networkFilter, setNetworkFilter] = useState<string>(searchParams.get("network") || "All");
  const [forumFilter, setForumFilter] = useState<string>(searchParams.get("forum") || "All");
  const [severityFilter, setSeverityFilter] = useState<string>(searchParams.get("severity") || "All");
  const [categoryFilter, setCategoryFilter] = useState<string>(searchParams.get("category") || "");
  const [targetsFilter, setTargetsFilter] = useState<string>(searchParams.get("targets") || "");
  const [targetTypeFilter, setTargetTypeFilter] = useState<string>(searchParams.get("target_type") || "");
  const [targetValueFilter, setTargetValueFilter] = useState<string>(searchParams.get("target_value") || "");
  const [includeContentFilter, setIncludeContentFilter] = useState<boolean>(searchParams.get("include_content") === "true");

  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [page, setPage] = useState(1);

  // Sync searchParams with states when query parameters change
  useEffect(() => {
    const sectorParam = searchParams.get("sector");
    if (sectorParam) {
      setSectorFilter(sectorParam);
      setPage(1);
    }
    const networkParam = searchParams.get("network");
    if (networkParam) {
      setNetworkFilter(networkParam);
      setPage(1);
    }
    const forumParam = searchParams.get("forum");
    if (forumParam) {
      setForumFilter(forumParam);
      setPage(1);
    }
    const severityParam = searchParams.get("severity");
    if (severityParam) {
      setSeverityFilter(severityParam);
      setPage(1);
    }
  }, [searchParams]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterValue(filterValue);
    }, 500);
    return () => clearTimeout(handler);
  }, [filterValue]);

  // Fetch forums and sectors
  useEffect(() => {
    fetchForums().then(setForums).catch(console.error);
    fetchSectors().then(setSectors).catch(console.error);
  }, []);

  const uniqueForumsList = React.useMemo(() => {
    const result: string[] = [];
    const seen = new Set<string>();

    forums.forEach((f) => {
      const display = getDisplayForum(f.forum, null);
      if (display && !seen.has(display) && display !== "Sector Intelligence") {
        seen.add(display);
        result.push(display);
      }
    });

    return result;
  }, [forums]);

  // SWR Fetcher
  const fetcher = async ({
    page,
    rowsPerPage,
    search,
    forum,
    network,
    sector,
    category,
    targetType,
    targetValue,
    targets,
    includeContent,
  }: {
    page: number;
    rowsPerPage: number;
    search: string;
    forum?: string;
    network?: string;
    sector?: string;
    category?: string;
    targetType?: string;
    targetValue?: string;
    targets?: string;
    includeContent?: boolean;
  }) => {
    const skip = (page - 1) * rowsPerPage;
    const params: LeakQueryParams = {
      limit: rowsPerPage,
      skip,
      search,
      forum: forum !== "All" ? forum : undefined,
      network: network !== "All" ? network?.toLowerCase() : undefined,
      sector: sector !== "All" ? sector : undefined,
      category: category || undefined,
      target_type: targetType || undefined,
      target_value: targetValue || undefined,
      targets: targets || undefined,
      include_content: includeContent || undefined,
    };
    return fetchLeaks(params);
  };

  const { data, isLoading } = useSWR(
    {
      page,
      rowsPerPage,
      search: debouncedFilterValue,
      forum: forumFilter,
      network: networkFilter,
      sector: sectorFilter,
      category: categoryFilter,
      targetType: targetTypeFilter,
      targetValue: targetValueFilter,
      targets: targetsFilter,
      includeContent: includeContentFilter,
    },
    fetcher,
    { keepPreviousData: true }
  );

  const totalPages = React.useMemo(() => {
    return data?.total ? Math.ceil(data.total / rowsPerPage) : 0;
  }, [data?.total, rowsPerPage]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSectorFilter("All");
    setNetworkFilter("Tor");
    setForumFilter("All");
    setSeverityFilter("All");
    setFilterValue("");
    setPage(1);
  };

  const hasActiveFilters =
    sectorFilter !== "All" ||
    networkFilter !== "Tor" ||
    forumFilter !== "All" ||
    severityFilter !== "All" ||
    filterValue !== "";

  const handleExport = async () => {
    // TODO: Implement CSV/JSON export functionality
  };

  const startResult = (page - 1) * rowsPerPage + 1;
  const endResult = Math.min(page * rowsPerPage, data?.total || 0);

  return (
    <div className="leaks-page">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="leaks-inner"
      >
        <FeedTopSection
          filters={{
            search: filterValue,
            category: categoryFilter,
            threatActor: forumFilter === "All" ? undefined : forumFilter,
            targets: targetsFilter,
            includeContent: includeContentFilter,
            sector: sectorFilter === "All" ? undefined : sectorFilter,
            network: networkFilter === "All" ? undefined : networkFilter,
            severity: severityFilter === "All" ? undefined : severityFilter,
          }}
          onChange={(f: FilterState) => {
            setFilterValue(f.search || "");
            setDebouncedFilterValue(f.search || "");
            setCategoryFilter(f.category || "");
            setForumFilter(f.threatActor || "All");
            setTargetsFilter(f.targets || "");
            setTargetTypeFilter((f as any).targetType || "");
            setTargetValueFilter((f as any).targetValue || "");
            setIncludeContentFilter(f.includeContent || false);
            setSectorFilter(f.sector || "All");
            setNetworkFilter(f.network || "All");
            setSeverityFilter(f.severity || "All");
            setPage(1);
            
            // Sync to URL
            const p = new URLSearchParams();
            if (f.search) p.set("search", f.search);
            if (f.category) p.set("category", f.category);
            if (f.threatActor) p.set("forum", f.threatActor);
            if (f.targets) p.set("targets", f.targets);
            if ((f as any).targetType) p.set("target_type", (f as any).targetType);
            if ((f as any).targetValue) p.set("target_value", (f as any).targetValue);
            if (f.includeContent) p.set("include_content", "true");
            if (f.sector) p.set("sector", f.sector);
            if (f.network) p.set("network", f.network);
            if (f.severity) p.set("severity", f.severity);
            
            // Need to update searchParams
            window.history.replaceState(null, '', `?${p.toString()}`);
          }}
          onClear={() => {
            setFilterValue("");
            setDebouncedFilterValue("");
            setCategoryFilter("");
            setForumFilter("All");
            setTargetsFilter("");
            setTargetTypeFilter("");
            setTargetValueFilter("");
            setIncludeContentFilter(false);
            setSectorFilter("All");
            setNetworkFilter("Tor");
            setSeverityFilter("All");
            setPage(1);
            window.history.replaceState(null, '', window.location.pathname);
          }}
        />
        
        <div className="leaks-filters-row">
          <div className="leaks-filters-left flex items-center gap-2 flex-wrap">
            {data?.search_metadata?.industry && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-xs font-medium text-indigo-300">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                <span>Industry: <strong className="text-white">{data.search_metadata.industry}</strong></span>
                {data.search_metadata.expanded_terms && data.search_metadata.expanded_terms.length > 0 && (
                  <span className="text-slate-400">
                    (Expanded: {data.search_metadata.expanded_terms.slice(0, 3).join(", ")})
                  </span>
                )}
              </span>
            )}
            {data?.query_time_ms !== undefined && (
              <span className="text-xs text-slate-500 font-mono">
                {data.query_time_ms}ms
              </span>
            )}
          </div>
          <div className="leaks-filters-right">
            {/* Results count badge */}
            <span className="leaks-results-badge">
              <span className="leaks-results-count">
                {data?.total_capped ? "10,000+" : (data?.total?.toLocaleString() ?? "—")}
              </span>
              &nbsp;leaks found
            </span>
            {/* Rows per page */}
            <div className="leaks-rows-wrap">
              <span className="leaks-rows-label">Rows:</span>
              <select
                id="rows-per-page"
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                className="leaks-rows-select"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        <div className="relative">
          {isLoading && data && (
            <div className="absolute top-0 left-0 w-full h-0.5 bg-indigo-500/20 z-10 overflow-hidden">
              <div className="h-full bg-indigo-500 w-1/3 animate-pulse relative" style={{ animation: "slide 1s ease-in-out infinite" }}>
                <style>{`@keyframes slide { 0% { left: -33%; } 100% { left: 100%; } }`}</style>
              </div>
            </div>
          )}
          <LeakTable
            leaks={data?.results || []}
            loading={isLoading && !data}
            isAdmin={user?.role === "admin"}
            user={user}
            hasSearch={Boolean(debouncedFilterValue?.trim()) || Boolean(searchParams.get("search")?.trim()) || Boolean(targetsFilter?.trim()) || Boolean(targetValueFilter?.trim())}
            onRowClick={(leak, isLocked, rank) => {
              const isSearching = Boolean(debouncedFilterValue?.trim()) || Boolean(searchParams.get("search")?.trim()) || Boolean(targetsFilter?.trim()) || Boolean(targetValueFilter?.trim());
              navigate(`/leaks/${leak._id}?rank=${rank || 0}${isSearching ? "&search=1" : ""}`, {
                state: { leakRank: rank, isLocked: isLocked || isSearching, fromSearch: isSearching },
              });
            }}
            startIndex={startResult}
          />
        </div>

        {/* ── Pagination Footer ─────────────────────────────────────── */}
        <div className="leaks-pagination-footer">
          <div className="leaks-pager-info">
            Showing{" "}
            <span className="leaks-pager-accent">{startResult}</span>
            {" – "}
            <span className="leaks-pager-accent">{endResult}</span>
            {" of "}
            <span className="leaks-pager-accent">
              {data?.total_capped ? "10,000+" : (data?.total?.toLocaleString() ?? 0)}
            </span>{" "}
            results
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      </motion.div>
    </div>
  );
}
